import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { ChevronRight, ChevronLeft, X, ShieldCheck, Zap, Database, Terminal } from 'lucide-react';
import { cn } from "@/lib/utils";

const steps = [
  {
    title: "Your Building Assistant",
    content: "Welcome to Sonas. Describe the game mechanics you want to build, and our AI assistant will help you plan and script them. It understands Roblox Studio hierarchy perfectly.",
    icon: Terminal,
    color: "text-sonas-accent",
    bg: "bg-sonas-accent/10"
  },
  {
    title: "My Possessions",
    content: "Save your favorite scripts and logic patterns in your Possessions. Sonas learns from these saved items to ensure it builds things exactly the way you like them in future projects.",
    icon: Database,
    color: "text-blue-500",
    bg: "bg-blue-500/10"
  },
  {
    title: "Studio Connector",
    content: "By using the Sonas companion plugin, scripts generated here can be sent directly into your Roblox Studio session. No more copying and pasting—just click 'Apply' and start testing.",
    icon: ShieldCheck,
    color: "text-purple-500",
    bg: "bg-purple-500/10"
  },
  {
    title: "Creator Credits",
    content: "Building with Sonas uses credits. Pro members get a larger weekly refill and access to advanced planning tools that can solve the most difficult scripting challenges.",
    icon: Zap,
    color: "text-amber-500",
    bg: "bg-amber-500/10"
  }
];

const Walkthrough = ({ open, onComplete, isProUpdate = false }) => {
  const [currentStep, setCurrentStep] = useState(0);

  if (!open) return null;

  const step = steps[currentStep];
  const Icon = step.icon;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(curr => curr + 1);
    } else {
      onComplete();
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-xl">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          className="liquid-glass max-w-lg w-full rounded-[48px] p-12 border border-white/5 relative overflow-hidden shadow-[0_0_100px_rgba(0,0,0,0.5)]"
        >
          <div className={cn("absolute -top-24 -left-24 w-64 h-64 blur-[80px] opacity-20 rounded-full transition-colors duration-500", step.bg)} />

          <div className="absolute top-0 right-0 p-8">
            <button onClick={onComplete} className="text-sonas-dim hover:text-sonas-text transition-colors">
              <X size={20} />
            </button>
          </div>

          <div className="space-y-10 relative z-10">
            <div className={cn("h-20 w-20 rounded-[32px] flex items-center justify-center border border-white/5 shadow-inner transition-colors duration-500", step.bg)}>
              <Icon size={40} className={step.color} />
            </div>

            <div className="space-y-4">
              <h2 className="text-4xl font-syne font-bold tracking-tight">
                {isProUpdate && currentStep === 0 ? "Pro Access Active" : step.title}
              </h2>
              <p className="text-sonas-muted text-lg leading-relaxed font-medium">
                {isProUpdate && currentStep === 0 
                  ? "Your account has been upgraded to Pro. You now have priority access to image recognition and personalized building instructions." 
                  : step.content}
              </p>
            </div>

            <div className="flex items-center justify-between pt-6 border-t border-white/5">
              <div className="flex gap-2">
                {steps.map((_, i) => (
                  <div 
                    key={i} 
                    className={cn(
                      "h-1.5 rounded-full transition-all duration-500",
                      i === currentStep ? "w-8 bg-sonas-accent" : "w-1.5 bg-white/10"
                    )}
                  />
                ))}
              </div>

              <div className="flex gap-4">
                {currentStep > 0 && (
                  <Button 
                    variant="ghost" 
                    onClick={() => setCurrentStep(curr => curr - 1)}
                    className="rounded-2xl h-14 px-8 border border-white/5 hover:bg-white/5"
                  >
                    <ChevronLeft size={20} />
                  </Button>
                )}
                <Button 
                  onClick={handleNext}
                  className="bg-sonas-accent text-sonas-bg font-bold rounded-2xl h-14 px-10 shadow-xl shadow-sonas-accent/20 gap-3 hover:scale-105 active:scale-95 transition-all"
                >
                  {currentStep === steps.length - 1 ? "Start Building" : "Next Phase"} 
                  <ChevronRight size={18} />
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default Walkthrough;